import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import SeoSocialTags from '../components/SeoSocialTags';
import '../styles/BlogPrimitives.css';

const BLOG_POST_CANONICAL_URL = 'https://circumvent.io/blog/soc-2-type-1-compliance';
const BLOG_POST_PUBLISHED_AT = '2026-05-07';
/** Lead, headings, body, and CTA (~256 words). Read time: max(1, round(words / 200 wpm)). */
const BLOG_POST_READ_TIME = '1 min read';
const BLOG_POST_AUTHOR = 'Circumvent Team';
const SOC_BADGE_IMAGE_SRC = '/images/blog/soc2-type1-sensiba-badge-white.png';
const BLOG_POST_OG_IMAGE_URL = 'https://circumvent.io/images/blog/soc2-type1-sensiba-badge-white.png';
const BLOG_POST_OG_TITLE = 'Circumvent Achieves SOC 2 Type 1 Compliance | Circumvent';
const BLOG_POST_OG_DESCRIPTION =
  'Circumvent has achieved SOC 2 Type 1 compliance, strengthening our commitment to secure-by-design cloud security operations.';

const BlogSoc2CompliancePage = () => {
  return (
    <>
      <Helmet>
        <title>{BLOG_POST_OG_TITLE}</title>
        <meta name="description" content={BLOG_POST_OG_DESCRIPTION} />
        <link rel="canonical" href={BLOG_POST_CANONICAL_URL} />
      </Helmet>
      <SeoSocialTags
        title={BLOG_POST_OG_TITLE}
        description={BLOG_POST_OG_DESCRIPTION}
        url={BLOG_POST_CANONICAL_URL}
        ogType="article"
        articlePublishedTime={`${BLOG_POST_PUBLISHED_AT}T00:00:00.000Z`}
        ogImageUrl={BLOG_POST_OG_IMAGE_URL}
      />

      <main className="page page--light blog-post-page">
        <section className="page-section page-section--white" aria-labelledby="soc2-post-heading">
          <article className="page-container blog-post">
            <div className="blog-post__header">
              <Link to="/blog" className="blog-crumb-link">
                <span aria-hidden="true">←</span> Blog
              </Link>
              <h1 id="soc2-post-heading" className="blog-post__title">
                Circumvent Achieves SOC 2 Type 1 Compliance
              </h1>
              <p className="blog-post__lead">
                Strengthening Our Commitment to Cloud Security
              </p>

              <ul className="blog-meta-list blog-meta-list--header" aria-label="Post details">
                <li>Compliance update</li>
                <li>
                  <time dateTime={BLOG_POST_PUBLISHED_AT}>7 May 2026</time>
                </li>
                <li>{BLOG_POST_READ_TIME}</li>
                <li>By {BLOG_POST_AUTHOR}</li>
              </ul>
            </div>

            <div className="blog-post__layout">
              <div className="blog-post__content">
                <figure className="blog-post__feature-image">
                  <img
                    src={SOC_BADGE_IMAGE_SRC}
                    alt="Sensiba SOC 2 Type 1 badge."
                    width={535}
                    height={247}
                    loading="eager"
                    decoding="async"
                  />
                </figure>

                <p>
                  At Circumvent, our mission is to simplify Cloud applications and infrastructure security. Today, we are
                  proud to take a significant step in that journey by announcing the successful completion of our System
                  and Organization Controls (SOC) 2 Type 1 examination.
                </p>

                <h2 id="what-this-means">What This Means for Our Customers and Partners</h2>
                <p>
                  The audit, conducted by the independent firm Sensiba, confirms that Circumvent&apos;s internal security
                  controls are suitably designed to meet the Trust Services Criteria for Security, Availability, and
                  Confidentiality.
                </p>
                <p>
                  As a platform that eliminates alert fatigue through automated and multi-agentic triage and remediation, we
                  understand the critical importance of the data we handle. This attestation provides our users and partners
                  with third-party verification that our system and processes are built with a security-first
                  mindset.
                </p>

                <h2 id="our-journey-to-trust">Our Journey to Trust</h2>
                <p>
                  The SOC 2 Type 1 report evaluates the design of our controls as of March 31, 2026. This milestone ensures
                  that the foundation of our platform and services are built on industry-leading standards.
                </p>
                <p>
                  Completing our SOC 2 Type 1 examination isn&apos;t just about a badge; it&apos;s about validating the rigorous
                  standards we&apos;ve set for ourselves and our customers from day one.
                </p>

                <h2 id="whats-next">What&apos;s Next?</h2>
                <p>
                  Security is an ongoing obligation, not a one-time event. We are already looking ahead to our SOC 2 Type 2
                  examination to further demonstrate the implementation and operating effectiveness of our controls over time.
                </p>

                <div className="blog-post__cta">
                  <h3>Request the SOC 2 report</h3>
                  <p>
                    To request a copy of our SOC 2 Type 1 report under an NDA, please get in touch.
                  </p>
                  <Link to="/#contact" className="btn btn-solid">
                    Contact us
                  </Link>
                </div>

                <Link to="/blog" className="blog-crumb-link">
                  <span aria-hidden="true">←</span> Blog
                </Link>
              </div>

              <aside className="blog-post__sidebar" aria-label="Post navigation">
                <nav className="blog-toc" aria-label="On this page">
                  <p className="blog-toc__title">On this page</p>
                  <ul className="blog-toc__list">
                    <li className="blog-toc__item">
                      <a className="blog-toc__link" href="#what-this-means">What This Means for Our Customers and Partners</a>
                    </li>
                    <li className="blog-toc__item">
                      <a className="blog-toc__link" href="#our-journey-to-trust">Our Journey to Trust</a>
                    </li>
                    <li className="blog-toc__item">
                      <a className="blog-toc__link" href="#whats-next">What&apos;s Next?</a>
                    </li>
                  </ul>
                </nav>
              </aside>
            </div>
          </article>
        </section>
      </main>
    </>
  );
};

export default BlogSoc2CompliancePage;
